import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const OPC = () => {
  const data = {
    title: 'One Person Company (OPC) Registration',
    description: 'Start your business as a sole entrepreneur with limited liability protection. OPC combines the benefits of sole proprietorship with corporate structure advantages.',
    keywords: 'OPC registration, One Person Company, single person company, solo entrepreneur, company registration India, limited liability',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          One Person Company (OPC) is a revolutionary business structure introduced under the Companies Act 2013, 
          allowing a single entrepreneur to operate a corporate entity with limited liability. Before OPC, sole 
          entrepreneurs had to either start a proprietorship (unlimited liability) or find a partner/co-director. 
          OPC bridges this gap by providing corporate benefits to individual business owners.
        </p>
        <p>
          <strong>Why Choose OPC?</strong> OPC is perfect for solo entrepreneurs, freelancers, consultants, and 
          small business owners who want the credibility of a company structure without multiple shareholders. 
          It offers limited liability protection, separating personal assets from business risks, while allowing 
          complete control over business decisions.
        </p>
        <p>
          <strong>Legal Framework:</strong> OPC is governed by Section 2(62) of Companies Act 2013 and regulated 
          by the Ministry of Corporate Affairs. It requires only one member (shareholder) and one director 
          (can be the same person). A nominee must be appointed who will become the member in case of death 
          or incapacity of the sole member.
        </p>
        <p>
          <strong>Budget 2021 Changes:</strong> The government has made OPC more attractive by removing the 
          residency requirement for Indian citizens, removing paid-up capital and turnover limits for conversion, 
          and reducing the residency requirement from 182 days to 120 days for Non-Resident Indians (NRIs).
        </p>
      </div>
    ),
    
    benefits: [
      'Single person can own and manage a company – complete control over decisions',
      'Limited liability protection – personal assets are safe from business liabilities',
      'Separate legal entity with perpetual succession',
      'No minimum paid-up capital requirement – start with any amount',
      'Easier to manage with fewer compliance requirements than Private Limited',
      'Professional corporate image – enhances credibility with clients and banks',
      'Can be converted to Private Limited Company as business grows',
      'Easier to raise loans compared to proprietorship',
      'Tax benefits similar to Private Limited Company (25% for turnover < ₹400 cr)',
      'No requirement for Annual General Meeting (AGM)',
      'NRIs and foreign citizens can now form OPC in India',
      'Business continuity through nominee member system',
    ],
    
    documents: [
      'PAN Card of the member and nominee (mandatory)',
      'Aadhaar Card of the member (for identity verification)',
      'Passport (for NRIs/foreign nationals) with valid Visa',
      'Passport size photographs (recent, colored - 2 copies)',
      'Address proof of registered office: Rent agreement (notarized) or Sale deed',
      'Utility bill of registered office (electricity/water bill - not older than 2 months)',
      'NOC (No Objection Certificate) from property owner',
      'Digital Signature Certificate (DSC) of the member',
      'Nominee consent letter (Form INC-3)',
      'Proof of identity and address of nominee',
      'Declaration by subscriber and first director',
    ],
    
    process: [
      'Obtain Digital Signature Certificate (DSC) for the member – 1-2 days',
      'Apply for Director Identification Number (DIN) with MCA – 1 day',
      'Name reservation through RUN (Reserve Unique Name) – 1-2 days',
      'Drafting of MOA (Memorandum of Association) and AOA (Articles of Association)',
      'Preparation of nominee consent and other incorporation documents',
      'Filing of SPICe+ form with MCA for incorporation',
      'Automatic application for PAN and TAN',
      'Certificate of Incorporation issued with CIN',
      'Obtain DIN for the sole director (if same as member)',
      'Open current bank account for the company',
    ],
    
    timeline: '10-15 Working Days',
    pricing: '₹5,499 + Govt Fees',
    
    additionalInfo: (
      <div className="space-y-3">
        <p><strong>Government Fees (Approximate):</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Authorized Capital up to ₹1 Lakh: ₹1,000 (Stamp Duty) + ₹500 (Filing)</li>
          <li>Name Reservation Fee: ₹1,000</li>
          <li>DSC (for member): ₹1,500 - ₹2,000</li>
          <li>Professional Tax Registration (varies by state)</li>
        </ul>
        <p className="mt-3"><strong>OPC Compliance Requirements:</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>No AGM required – only board meeting minutes needed</li>
          <li>Minimum 2 board meetings per year (6 months gap maximum)</li>
          <li>Annual filing: Form AOC-4 and MGT-7A (simplified annual return)</li>
          <li>Income Tax Return filing by 31st July (or 30th September if audit)</li>
          <li>Audit mandatory if turnover > ₹2 crore or capital > ₹50 lakhs</li>
        </ul>
        <p className="mt-3"><strong>Conversion Thresholds (Voluntary):</strong></p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Can voluntarily convert to Private Limited anytime</li>
          <li>Mandatory conversion removed by Budget 2021</li>
          <li>Minimum 2 years required before voluntary conversion</li>
        </ul>
      </div>
    ),
    
    faqs: [
      {
        question: 'Who can form an OPC in India?',
        answer: 'Any natural person who is an Indian citizen can form an OPC. After Budget 2021, NRIs can also form OPC if they have stayed in India for 120 days in the preceding financial year. Foreign nationals (non-residents) cannot form OPC.',
      },
      {
        question: 'Is a nominee mandatory for OPC?',
        answer: 'Yes, every OPC must have a nominee who will become the member in case of death or incapacity of the sole member. The nominee must give consent in Form INC-3. The nominee can be changed later by filing Form INC-4.',
      },
      {
        question: 'What are the compliance requirements for OPC?',
        answer: 'OPC has to file annual returns (MGT-7A), financial statements (AOC-4), and Income Tax Return. Minimum 2 board meetings per year. No AGM required. Audit required if turnover > ₹2 crore or capital > ₹50 lakhs.',
      },
      {
        question: 'Can OPC have employees?',
        answer: 'Yes, OPC can hire unlimited employees. The sole member/director manages the company, but can employ staff for operations. PF, ESI, and other labor compliances apply based on employee count.',
      },
      {
        question: 'What is the difference between OPC and Proprietorship?',
        answer: 'OPC is a company with limited liability and separate legal entity status, while proprietorship has unlimited liability. OPC can raise loans easier, has better credibility, but higher compliance. Proprietorship is simpler but riskier.',
      },
      {
        question: 'Can OPC raise funds from investors?',
        answer: 'OPC cannot have more than one member, so it cannot issue shares to investors. For raising funds, OPC should be converted to Private Limited Company. Bank loans are easier with OPC compared to proprietorship.',
      },
      {
        question: 'What happens if the sole member dies?',
        answer: 'If the sole member dies or becomes incapacitated, the nominee automatically becomes the member of OPC. The nominee can continue the business or transfer membership to legal heirs. This ensures business continuity.',
      },
      {
        question: 'Can same person be member, director, and nominee?',
        answer: 'The same person can be member and director, but nominee must be a different person. If the sole member dies, the nominee takes over, so they cannot be the same person for practical reasons.',
      },
    ],
  };

  return <ServiceTemplate {...data} />;
};

export default OPC;
